const DietProductionStageAssignmentSocket = (
	json: IDietMealProductionStageAssignmentSocket,
): IDietMealProductionStageAssignmentSocketModel => {
	const { is_active, updated_at, created_at, ...rest } = json

	return {
		...rest,
		isActive: is_active,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

const DietProductionStageAssignmentSocketStage = (
	json: IDietMealProductionStageAssignmentSocketStage,
): IDietMealProductionStageAssignmentSocketStageModel => {
	const { is_active, created_at, updated_at, ...rest } = json

	return {
		...rest,
		isActive: is_active,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

const DietProductionStageAssignmentMachine = (
	json: IDietMealProductionStageAssignmentMachine,
): IDietMealProductionStageAssignmentMachineModel => {
	const { short_name, is_active, updated_at, created_at, ...rest } = json

	return {
		...rest,
		isActive: is_active,
		shortName: short_name,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

const DietProductionStageAssignmentQualityControlParameter = (
	json: IDietMealProductionStageAssignmentQualityControlParameter,
): IDietMealProductionStageAssignmentQualityControlParameterModel => {
	const { production_machine_id, created_at, updated_at, ...rest } = json

	return {
		...rest,
		productionMachineId: production_machine_id,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

const DietProductionStageAssignmentQualityControl = (
	json: IDietMealProductionStageAssignmentQualityControl,
): IDietMealProductionStageAssignmentQualityControlModel => {
	const {
		production_stage_assignment_id,
		quality_control_parameter,
		quality_control_parameter_id,
		created_at,
		updated_at,
		...rest
	} = json

	return {
		...rest,
		productionStageAssignmentId: production_stage_assignment_id,
		qualityControlParameterId: quality_control_parameter_id,
		qualityControlParameter: DietProductionStageAssignmentQualityControlParameter(quality_control_parameter),
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

const DietProductionStageAssignmentMachineType = (
	json: IDietMealProductionStageAssignmentMachineType,
): IDietMealProductionStageAssignmentMachineTypeModel => {
	const { id, name, description, is_active, machines, created_at, updated_at } = json

	return {
		id,
		name,
		description,
		isActive: is_active,
		machines: Array.isArray(machines) ? machines.map(DietProductionStageAssignmentMachine) : [],
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const DietProductionStageCuttingType = (
	json: IDietProductionStageCuttingType,
): IDietProductionStageCuttingTypeModel => {
	const { value, label } = json

	return {
		value,
		label,
	}
}

const DietProductionStageAssignmentCuttingType = (
	json: IDietProductionStageAssignmentCuttingType,
): IDietProductionStageAssignmentCuttingTypeModel => {
	const { id, cutting_type, dietary_production_stage_assignment_id, created_at } = json

	return {
		id,
		cuttingType: cutting_type,
		dietaryProductionStageAssignmentId: dietary_production_stage_assignment_id,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

const DietProductionStageAssignmentComponentWot = (
	json: IDietProductionStageComponentWot,
): IDietProductionStageComponentWotModel => {
	const { model_id, model_type, weight_percentage_after_stage, created_at, updated_at } = json

	return {
		modelId: model_id,
		modelType: model_type,
		weightPercentageAfterStage: weight_percentage_after_stage,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const DietProductionStageAssignment = (
	json: IDietMealProductionStageAssignment,
): IDietMealProductionStageAssignmentModel => {
	const {
		id,
		order,
		model_id,
		model_type,
		owner_id,
		owner_type,
		group_id,
		machine_types,
		product_type,
		production_socket_id,
		production_stage_id,
		production_socket,
		production_stage,
		quantity_by_time,
		quality_control_parameters,
		weight_percentage_after_stage,
		weight_waste,
		cutting,
		component_wots,
		updated_at,
		created_at,
		...rest
	} = json

	return {
		...rest,
		id: id ?? undefined,
		order,
		modelId: model_id ?? null,
		modelType: model_type ?? null,
		groupId: group_id ?? null,
		ownerId: owner_id,
		ownerType: owner_type,
		quantityByTime: quantity_by_time,
		productionSocketId: production_socket_id,
		productionSocket: DietProductionStageAssignmentSocket(production_socket),
		productionStageId: production_stage_id,
		productionStage: DietProductionStageAssignmentSocketStage(production_stage),
		machineTypes: Array.isArray(machine_types) ? machine_types.map(DietProductionStageAssignmentMachineType) : [],
		productType: product_type ? ProductionProductType(product_type) : null,
		qualityControlParameters: Array.isArray(quality_control_parameters)
			? quality_control_parameters.map(DietProductionStageAssignmentQualityControl)
			: [],
		weightWaste: weight_waste,
		weightPercentageAfterStage: weight_percentage_after_stage,
		cutting: cutting ? DietProductionStageAssignmentCuttingType(cutting) : null,
		componentWots: Array.isArray(component_wots)
			? component_wots.map(DietProductionStageAssignmentComponentWot)
			: [],
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

const buildStageWeightPayload = (weights: IDietProductionStageItemForm['weights']) => {
	const isOutput = weights.type === EProductionStageWeightType.OUTPUT
	return {
		weight_waste: isOutput ? null : weights.waste,
		weight_percentage_after_stage: isOutput ? (weights.output ?? 100) : null,
	}
}

const MODEL_TYPE_MAP: Record<EProducutionStagesType, EDietSharedComponentType | null> = {
	meal: null,
	'semi-product': null,
	group: EDietSharedComponentType.GROUP,
	ingredient: EDietSharedComponentType.MATERIAL,
}

const mapModelType = (
	stage: IDietProductionStageItemForm,
	productionType: EProducutionStagesType,
): EDietSharedComponentType | null => {
	if (productionType === EProducutionStagesType.SEMI_PRODUCT && stage.ingredient) {
		return EDietSharedComponentType.SEMIPRODUCT
	}

	return MODEL_TYPE_MAP[productionType] ?? null
}

const buildQualityControlParams = (
	parameters: IDietProductionStageItemForm['parameters'],
): IDietProductionStageQualityControlParameterPayload[] =>
	parameters.map(parameter => ({
		quality_control_parameter_id: parameter.productionPerformanceParameterId!,
		value: parameter.value!,
	}))

const resolveModelId = (
	modelType: EDietSharedComponentType | null,
	groupId: number | null,
	ingredient: IDietProductionStageItemForm['ingredient'],
): number | null => {
	if (modelType === EDietSharedComponentType.GROUP) return groupId

	return ingredient?.id ?? null
}

const buildComponentWotsPayload = (
	componentWots: IDietProductionStageComponentWotForm[] | undefined,
): IDietProductionStageComponentWotPayload[] =>
	(componentWots ?? []).map(w => ({
		model_id: w.modelId,
		model_type: w.modelType,
		weight_percentage_after_stage: w.weightPercentageAfterStage,
	}))

const shouldSendComponentWots = (type: EProducutionStagesType, isDefault: boolean): boolean =>
	type === EProducutionStagesType.GROUP || (type === EProducutionStagesType.SEMI_PRODUCT && isDefault)

const buildStagePayload = (
	stage: IDietProductionStageItemForm,
	type: EProducutionStagesType,
	groupId: number | null,
	readonlyCount: number,
	isDefault: boolean,
): IDietProductionStagePayload => {
	const {
		id,
		socketId,
		socketStageId,
		machineIds,
		productTypeId,
		weights,
		duration,
		durationType,
		performanceDuration,
		cuttingType,
		description,
		ingredient,
		parameters,
		order,
		quantityByTime,
		componentWots,
	} = stage

	const isPerformanceMode = durationType === EProductionStageDurationType.PERFORMANCE
	const resolvedDuration = isPerformanceMode ? performanceDuration : duration
	const _order = readonlyCount ? order - readonlyCount : order
	const model_type = mapModelType(stage, type)

	const obj: IDietProductionStagePayload = {
		model_id: resolveModelId(model_type, groupId, ingredient),
		model_type,
		quality_control_parameters: buildQualityControlParams(parameters),
		id: id ?? undefined,
		order: _order,
		production_socket_id: socketId!,
		production_stage_id: socketStageId!,
		production_product_type_id: productTypeId?.value ?? null,
		production_machine_type_ids: machineIds?.map(machine => machine.value) ?? [],
		duration: resolvedDuration!,
		cutting_type: cuttingType ?? null,
		description: description ?? null,
		...buildStageWeightPayload(weights),
		quantity_by_time: isPerformanceMode ? quantityByTime : null,
	}

	if (groupId && obj.model_type !== EDietSharedComponentType.GROUP) {
		obj.group_id = groupId
	}

	if (shouldSendComponentWots(type, isDefault)) {
		obj.component_wots = buildComponentWotsPayload(componentWots)
	}

	return obj
}

export const DietProductionStageToPayload = (model: IDietProductionStageForm): IDietProductionStagePayload[] => {
	const { type, groupId, stages, isDefault } = model
	const readonlyCount = stages.filter(stage => stage.isReadonly).length

	return stages
		.filter(stage => !stage.isReadonly)
		.filter(stage => stage.isVisible)
		.map(stage => buildStagePayload(stage, type, groupId, readonlyCount, isDefault))
}
